/**
 * Submission Dialog Component
 *
 * Modal dialog for submitting expense reports with digital signature confirmation.
 * Shows expense summary and requires checkbox confirmation before submission.
 */

import { formatAmount as formatCurrency } from '../utils/formatters.js';
import { toastError } from './toast.js';
import type { ExpenseReport, ReceiptImage } from '../../backend/types/database.js';

export interface BudgetSummary {
  totalSpent: number;
  approvedBudget: number;
  underBudget: boolean;
  overBudgetAmount?: number;
}

export interface SubmissionDialogProps {
  report: ExpenseReport;
  receipts: ReceiptImage[];
  budgetSummary: BudgetSummary;
  linkedRequestTitle?: string;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
}

/**
 * Create the submission dialog component
 */
export function createSubmissionDialog(props: SubmissionDialogProps): HTMLElement {
  const { report: _report, receipts, budgetSummary, linkedRequestTitle, onSubmit, onCancel } = props;

  // Create overlay container
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4';

  // Create dialog container
  const dialog = document.createElement('div');
  dialog.className = 'bg-neutral-900 border border-neutral-800 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col';

  // Header
  const header = document.createElement('div');
  header.className = 'p-6 border-b border-neutral-800';

  const title = document.createElement('h2');
  title.className = 'text-xl font-semibold text-neutral-100';
  title.textContent = 'Submit Expense Report';

  header.appendChild(title);
  dialog.appendChild(header);

  // Content container (scrollable)
  const content = document.createElement('div');
  content.className = 'flex-1 overflow-y-auto p-6 space-y-6';

  // Summary section
  const summarySection = createSummarySection(receipts.length, budgetSummary, linkedRequestTitle);
  content.appendChild(summarySection);

  // Receipt list
  const receiptList = createReceiptList(receipts);
  content.appendChild(receiptList);

  // Budget status
  const budgetStatus = createBudgetStatus(budgetSummary);
  content.appendChild(budgetStatus);

  // Digital signature section
  const signatureSection = document.createElement('div');
  signatureSection.className = 'bg-neutral-800/50 rounded-lg p-4';

  const signatureLabel = document.createElement('label');
  signatureLabel.className = 'flex items-start gap-3 cursor-pointer';

  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.id = 'digital-signature-checkbox';
  checkbox.className = 'mt-1 w-5 h-5 rounded border-neutral-600 bg-neutral-800 text-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-0 focus:ring-offset-neutral-900';

  const labelText = document.createElement('div');
  labelText.className = 'flex-1';

  const labelTitle = document.createElement('div');
  labelTitle.className = 'font-medium text-neutral-100 mb-1';
  labelTitle.textContent = 'Digital Signature Required';

  const labelDescription = document.createElement('div');
  labelDescription.className = 'text-sm text-neutral-400 leading-relaxed';
  labelDescription.textContent = 'I confirm that these expenses are accurate, business-related, and comply with company policy. I understand that false claims may result in disciplinary action.';

  labelText.appendChild(labelTitle);
  labelText.appendChild(labelDescription);

  signatureLabel.appendChild(checkbox);
  signatureLabel.appendChild(labelText);
  signatureSection.appendChild(signatureLabel);
  content.appendChild(signatureSection);

  dialog.appendChild(content);

  // Footer with buttons
  const footer = document.createElement('div');
  footer.className = 'p-6 border-t border-neutral-800 bg-neutral-900/50 backdrop-blur-sm';

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'flex justify-end gap-3';

  // Cancel button
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'px-4 py-2 text-neutral-400 hover:text-neutral-100 transition-colors';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.onclick = () => {
    overlay.remove();
    onCancel();
  };

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.className = 'px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600';
  submitBtn.textContent = 'Submit Report';
  submitBtn.disabled = true;

  // Enable submit button only when checkbox is checked
  checkbox.addEventListener('change', () => {
    submitBtn.disabled = !checkbox.checked;
  });

  // Handle submission
  submitBtn.onclick = async () => {
    if (!checkbox.checked) return;

    submitBtn.disabled = true;
    const originalText = submitBtn.textContent;
    submitBtn.textContent = 'Submitting...';

    try {
      await onSubmit();
      overlay.remove();
    } catch (error: unknown) {
      console.error('Submission failed:', error);
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
      checkbox.checked = false;

      // Show error toast to user
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit expense report. Please try again.';
      toastError(errorMessage, { duration: 5000 });
    }
  };

  buttonContainer.appendChild(cancelBtn);
  buttonContainer.appendChild(submitBtn);
  footer.appendChild(buttonContainer);
  dialog.appendChild(footer);

  // Add dialog to overlay
  overlay.appendChild(dialog);

  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
      onCancel();
    }
  });

  // Close on Escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      overlay.remove();
      onCancel();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);

  return overlay;
}

/**
 * Create summary section
 */
function createSummarySection(
  receiptCount: number,
  budgetSummary: BudgetSummary,
  linkedRequestTitle?: string
): HTMLElement {
  const section = document.createElement('div');
  section.className = 'space-y-3';

  // Linked request
  if (linkedRequestTitle) {
    const requestDiv = document.createElement('div');
    requestDiv.className = 'flex justify-between items-start';

    const label = document.createElement('span');
    label.className = 'text-sm text-neutral-400';
    label.textContent = 'Expenditure Request:';

    const value = document.createElement('span');
    value.className = 'text-sm text-neutral-200 font-medium text-right max-w-xs';
    value.textContent = linkedRequestTitle;

    requestDiv.appendChild(label);
    requestDiv.appendChild(value);
    section.appendChild(requestDiv);
  }

  // Receipt count
  const countDiv = document.createElement('div');
  countDiv.className = 'flex justify-between';

  const countLabel = document.createElement('span');
  countLabel.className = 'text-sm text-neutral-400';
  countLabel.textContent = 'Total Receipts:';

  const countValue = document.createElement('span');
  countValue.className = 'text-sm text-neutral-200';
  countValue.textContent = receiptCount.toString();

  countDiv.appendChild(countLabel);
  countDiv.appendChild(countValue);
  section.appendChild(countDiv);

  // Total amount
  const totalDiv = document.createElement('div');
  totalDiv.className = 'flex justify-between';

  const totalLabel = document.createElement('span');
  totalLabel.className = 'text-sm text-neutral-400';
  totalLabel.textContent = 'Total Amount:';

  const totalValue = document.createElement('span');
  totalValue.className = 'text-lg font-semibold text-neutral-100';
  totalValue.textContent = formatCurrency(budgetSummary.totalSpent, 'CHF');

  totalDiv.appendChild(totalLabel);
  totalDiv.appendChild(totalValue);
  section.appendChild(totalDiv);

  return section;
}

/**
 * Create receipt list
 */
function createReceiptList(receipts: ReceiptImage[]): HTMLElement {
  const container = document.createElement('div');
  container.className = 'space-y-2';

  const title = document.createElement('h3');
  title.className = 'text-sm font-medium text-neutral-300 mb-2';
  title.textContent = 'Receipt Details';
  container.appendChild(title);

  const list = document.createElement('div');
  list.className = 'max-h-40 overflow-y-auto space-y-2 bg-neutral-800/30 rounded-lg p-3';

  if (receipts.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'text-sm text-neutral-500 text-center py-4';
    empty.textContent = 'No receipts attached';
    list.appendChild(empty);
  } else {
    receipts.forEach((receipt, index) => {
      const item = document.createElement('div');
      item.className = 'flex justify-between items-center py-2 px-2 hover:bg-neutral-800/50 rounded transition-colors';

      const info = document.createElement('div');
      info.className = 'flex-1 min-w-0';

      const vendor = document.createElement('div');
      vendor.className = 'text-sm text-neutral-200 truncate';
      vendor.textContent = receipt.vendor || `Receipt ${index + 1}`;

      const date = document.createElement('div');
      date.className = 'text-xs text-neutral-500';
      date.textContent = receipt.receipt_date
        ? new Date(receipt.receipt_date).toLocaleDateString()
        : 'Date not set';

      info.appendChild(vendor);
      info.appendChild(date);

      const amount = document.createElement('div');
      amount.className = 'text-sm font-medium text-neutral-100 ml-4';
      amount.textContent = formatCurrency(receipt.amount_chf ?? 0, 'CHF');

      item.appendChild(info);
      item.appendChild(amount);
      list.appendChild(item);
    });
  }

  container.appendChild(list);
  return container;
}

/**
 * Create budget status display
 */
function createBudgetStatus(budgetSummary: BudgetSummary): HTMLElement {
  const container = document.createElement('div');
  container.className = `p-3 rounded-lg border ${
    budgetSummary.underBudget
      ? 'bg-green-900/20 border-green-800/50'
      : 'bg-amber-900/20 border-amber-800/50'
  }`;

  const status = document.createElement('div');
  status.className = 'flex items-center justify-between';

  const label = document.createElement('span');
  label.className = `text-sm font-medium ${
    budgetSummary.underBudget ? 'text-green-400' : 'text-amber-400'
  }`;
  label.textContent = budgetSummary.underBudget ? 'Within Budget' : 'Over Budget';

  const details = document.createElement('span');
  details.className = 'text-sm text-neutral-400';

  if (budgetSummary.underBudget) {
    const remaining = budgetSummary.approvedBudget - budgetSummary.totalSpent;
    details.textContent = `${formatCurrency(remaining, 'CHF')} remaining`;
  } else {
    details.textContent = `${formatCurrency(budgetSummary.overBudgetAmount ?? 0, 'CHF')} over`;
  }

  status.appendChild(label);
  status.appendChild(details);
  container.appendChild(status);

  return container;
}